import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useServiceCatalog, SERVICE_CATEGORIES, type ServiceCatalogEntry } from '@/hooks/useServiceCatalog';
import { toast } from 'sonner';
import { Plus, Trash2, ArrowUp, ArrowDown, Pencil, Check, X, Loader2 } from 'lucide-react';

export function ServicesCatalogSettings() {
  const { catalog, isLoading, addService, updateService, deleteService } = useServiceCatalog();
  const [newName, setNewName] = useState('');
  const [newCategory, setNewCategory] = useState('other');
  const [newUnit, setNewUnit] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editCategory, setEditCategory] = useState('other');
  const [editUnit, setEditUnit] = useState('');

  const startEdit = (s: ServiceCatalogEntry) => {
    setEditingId(s.id);
    setEditName(s.name);
    setEditCategory(s.category);
    setEditUnit(s.unit_label || '');
  };

  const saveEdit = async (id: string) => {
    if (!editName.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    try {
      await updateService.mutateAsync({
        id,
        patch: { name: editName.trim(), category: editCategory, unit_label: editUnit.trim() || null },
      });
      toast.success('Service updated');
      setEditingId(null);
    } catch (e: any) {
      toast.error(e.message || 'Update failed');
    }
  };

  const handleAdd = async () => {
    if (!newName.trim()) {
      toast.error('Enter a service name');
      return;
    }
    try {
      await addService.mutateAsync({ name: newName.trim(), category: newCategory, unit_label: newUnit || null });
      toast.success(`Added "${newName.trim()}"`);
      setNewName(''); setNewUnit(''); setNewCategory('other');
    } catch (e: any) {
      toast.error(e.message || 'Could not add service');
    }
  };

  const handleArchiveToggle = async (s: ServiceCatalogEntry) => {
    try {
      await updateService.mutateAsync({ id: s.id, patch: { is_active: !s.is_active } });
      toast.success(s.is_active ? 'Service archived' : 'Service restored');
    } catch (e: any) {
      toast.error(e.message || 'Update failed');
    }
  };

  const handleDelete = async (s: ServiceCatalogEntry) => {
    if (!confirm(`Permanently delete "${s.name}"? Historical logs will keep their raw service type.`)) return;
    try {
      await deleteService.mutateAsync(s.id);
      toast.success('Service deleted');
    } catch (e: any) {
      toast.error(e.message || 'Delete failed');
    }
  };

  const move = async (s: ServiceCatalogEntry, dir: -1 | 1) => {
    const sorted = [...catalog].sort((a, b) => a.sort_order - b.sort_order);
    const idx = sorted.findIndex((x) => x.id === s.id);
    const swapWith = sorted[idx + dir];
    if (!swapWith) return;
    try {
      await Promise.all([
        updateService.mutateAsync({ id: s.id, patch: { sort_order: swapWith.sort_order } }),
        updateService.mutateAsync({ id: swapWith.id, patch: { sort_order: s.sort_order } }),
      ]);
    } catch (e: any) {
      toast.error(e.message || 'Reorder failed');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Services Catalog</CardTitle>
        <CardDescription>
          Add, rename, archive, or reorder the services your staff can log. Changes apply across all dashboards, the
          Services Breakdown, and the Summary Report export.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Add new service */}
        <div className="grid gap-3 sm:grid-cols-[1fr_160px_140px_auto] sm:items-end">
          <div className="space-y-1">
            <Label htmlFor="new-svc-name">New service name</Label>
            <Input
              id="new-svc-name"
              placeholder="e.g. Snacks, Bus Pass"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label>Category</Label>
            <Select value={newCategory} onValueChange={setNewCategory}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {SERVICE_CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Unit (optional)</Label>
            <Input placeholder="tickets, loads…" value={newUnit} onChange={(e) => setNewUnit(e.target.value)} />
          </div>
          <Button onClick={handleAdd} disabled={addService.isPending}>
            {addService.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
            Add
          </Button>
        </div>

        {/* Existing services */}
        {isLoading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : catalog.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No services yet — add your first one above.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]">Order</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...catalog].sort((a, b) => a.sort_order - b.sort_order).map((s, idx, arr) => {
                const isEditing = editingId === s.id;
                return (
                  <TableRow key={s.id} className={!s.is_active ? 'opacity-60' : ''}>
                    <TableCell>
                      <div className="flex flex-col">
                        <Button variant="ghost" size="icon" className="h-6 w-6" disabled={idx === 0} onClick={() => move(s, -1)}>
                          <ArrowUp className="h-3 w-3" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-6 w-6" disabled={idx === arr.length - 1} onClick={() => move(s, 1)}>
                          <ArrowDown className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-8" />
                      ) : (
                        <span className="font-medium">{s.name}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Select value={editCategory} onValueChange={setEditCategory}>
                          <SelectTrigger className="h-8 w-[130px]"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {SERVICE_CATEGORIES.map((c) => (
                              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-sm capitalize text-muted-foreground">{s.category}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Input value={editUnit} onChange={(e) => setEditUnit(e.target.value)} className="h-8 w-[120px]" placeholder="—" />
                      ) : (
                        <span className="text-sm text-muted-foreground">{s.unit_label || '—'}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {s.is_active ? (
                        <Badge variant="secondary">Active</Badge>
                      ) : (
                        <Badge variant="outline">Archived</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      {isEditing ? (
                        <>
                          <Button variant="ghost" size="icon" onClick={() => saveEdit(s.id)}><Check className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => setEditingId(null)}><X className="h-4 w-4" /></Button>
                        </>
                      ) : (
                        <>
                          <Button variant="ghost" size="icon" onClick={() => startEdit(s)} title="Rename"><Pencil className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => handleArchiveToggle(s)} title={s.is_active ? 'Archive' : 'Restore'}>
                            {s.is_active ? <X className="h-4 w-4" /> : <Check className="h-4 w-4" />}
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(s)} title="Delete">
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        <p className="text-xs text-muted-foreground">
          Renaming or archiving a service updates labels everywhere instantly. Historical service logs are preserved.
        </p>
      </CardContent>
    </Card>
  );
}
